// System Prompt Configuration Step
import React, { useState, useEffect , useCallback } from 'react';
import { Settings, Lightbulb, RotateCcw, Eye, RefreshCw } from 'lucide-react';
import { ResumeData } from '../../types';
import { promptService, AddonPrompt } from '../../services/promptService';

interface SystemPromptStepProps {
  systemPrompt: string;
  resumeData: ResumeData | null;
  jobDescription: string;
  onSystemPromptChange: (systemPrompt: string) => void;
}

const defaultPromptTemplate = 'No specific addditional Instructions. Only follow the main system default instructions for the assistance.';

export const SystemPromptStep: React.FC<SystemPromptStepProps> = ({
  systemPrompt,
  resumeData,
  jobDescription,
  onSystemPromptChange
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [addonPrompts, setAddonPrompts] = useState<AddonPrompt[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);
  const [promptsError, setPromptsError] = useState<string | null>(null);

  // Fetch addon prompts from backend
  useEffect(() => {
    fetchAddonPrompts();
  }, []);

  const fetchAddonPrompts = async (forceRefresh: boolean = false) => {
    setIsLoadingPrompts(true);
    setPromptsError(null);

    try {
      const response = await promptService.getAddonPrompts(forceRefresh);
      
      if (response.success) {
        setAddonPrompts(response.prompts);
      } else {
        setPromptsError('Failed to load addon prompts');
        // Fallback to empty array
        setAddonPrompts([]);
      }
    } catch (error) {
      console.error('Error fetching addon prompts:', error);
      setPromptsError('Error loading prompts');
      setAddonPrompts([]);
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  const generateDefaultPrompt = useCallback(() => {
    if (!resumeData) return;
  
    const prompt = `${defaultPromptTemplate}`;
  
    onSystemPromptChange(prompt);
  }, [resumeData, onSystemPromptChange]);
  

  useEffect(() => {
    if (!systemPrompt && resumeData && jobDescription) {
      generateDefaultPrompt();
    }
  }, [systemPrompt, generateDefaultPrompt, resumeData, jobDescription]);

  

  const handleTemplateSelect = (template: string) => {
    onSystemPromptChange(template);
  };


  const resetToDefault = () => {
    generateDefaultPrompt();
  };

  const handleRefreshPrompts = () => {
    fetchAddonPrompts(true);
  };

  const previewPrompt = systemPrompt;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Settings className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Personalized your Bot style
        </h2>
        <p className="text-gray-600">
          Customize how the AI should respond during your interview. These additional intructions guide the AI's behavior and tone.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Template Selection */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium text-gray-900">Choose a Response Style</h3>
            <button
              onClick={handleRefreshPrompts}
              disabled={isLoadingPrompts}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center disabled:opacity-50"
              title="Refresh prompts from database"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingPrompts ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Loading State */}
          {isLoadingPrompts && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-gray-600">Loading prompts...</span>
            </div>
          )}

          {/* Error State */}
          {promptsError && !isLoadingPrompts && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-600 text-sm">{promptsError}</p>
              <button
                onClick={() => fetchAddonPrompts()}
                className="mt-2 text-sm text-red-700 hover:text-red-800 font-medium"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Addon Prompts Grid */}
          {!isLoadingPrompts && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Default/None Option */}
              <button
                onClick={() => handleTemplateSelect(defaultPromptTemplate)}
                className="text-left p-4 border border-gray-200 rounded-lg transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <h4 className="font-medium text-gray-900">
                  Default
                  <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                    Standard
                  </span>
                </h4>
                <p className="text-sm mt-1 text-gray-600">
                  No additional instructions. Use system defaults only.
                </p>
              </button>

              {/* Addon Prompts from Database (slug = onboarding display title) */}
              {addonPrompts.map((prompt) => {
                const displayTitle = prompt.slug || prompt.title;
                return (
                  <button
                    key={prompt.title}
                    onClick={() => handleTemplateSelect(prompt.content)}
                    className="text-left p-4 border border-gray-200 rounded-lg transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    <h4 className="font-medium text-gray-900">
                      {displayTitle}
                    </h4>
                    <p className="text-sm mt-1 text-gray-600 line-clamp-2">
                      {prompt.content.substring(0, 100)}
                      {prompt.content.length > 100 ? '...' : ''}
                    </p>
                  </button>
                );
              })}

              {/* Custom Option */}
              <button
                onClick={() => handleTemplateSelect('')}
                className="text-left p-4 border border-purple-300 bg-purple-50 rounded-lg transition-colors hover:border-purple-400 hover:bg-purple-100"
              >
                <h4 className="font-medium text-purple-900">
                  Custom
                  <span className="ml-2 text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                    Flexible
                  </span>
                </h4>
                <p className="text-sm mt-1 text-purple-700">
                  Write your own custom instructions
                </p>
              </button>

              {/* Empty State */}
              {addonPrompts.length === 0 && !promptsError && (
                <div className="col-span-full text-center py-6 text-gray-500">
                  <p className="text-sm">No addon prompts available.</p>
                  <p className="text-xs mt-1">Custom prompt templates can be added to the database.</p>
                </div>
              )}
            </div>
          )}
        </div>


        {/* Custom Prompt Editor */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700">
              System Prompt (Additional Instructions for the AI)*
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
              >
                <Eye className="w-4 h-4 mr-1" />
                {showPreview ? 'Hide' : 'Preview'}
              </button>
              <button
                onClick={resetToDefault}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </button>
            </div>
          </div>
          <textarea
            id="systemPrompt"
            value={systemPrompt}
            onChange={(e) => onSystemPromptChange(e.target.value)}
            placeholder="Select a template above or write your own custom instructions here..."
            className="textarea-field min-h-[200px]"
          />
          <p className="text-xs text-gray-500 mt-2">
            <span className="font-medium">💡 Tip:</span> This prompt will guide how the AI responds during your interview. Be specific about the tone, style, and focus areas you want. You can select a template above and then customize it further.
          </p>
        </div>

        {/* Preview */}
        {showPreview && previewPrompt && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Prompt Preview</h3>
            <div className="text-sm text-gray-700 whitespace-pre-line max-h-60 overflow-y-auto">
              {previewPrompt}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Lightbulb className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium text-sm">Tips for Effective Prompts</p>
              <ul className="text-blue-700 text-xs mt-1 space-y-1">
                <li>• Be specific about the tone and style you want (professional, conversational, etc.)</li>
                <li>• Include instructions about using your experience and skills</li>
                <li>• Specify how to handle questions about unfamiliar topics</li>
                <li>• Mention any specific industry knowledge or terminology to use</li>
                <li>• Include guidance about response length and structure</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
